import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft,
  CalendarPlus,
  Calculator,
  CaretDown,
  DotsThreeVertical,
  EnvelopeSimple,
  FolderSimplePlus,
  PaperPlaneTilt,
  PencilSimple,
  X,
  Plus,
  Phone,
  ArrowsClockwise,
  DeviceMobile,
  Star,
  Trash,
  Wrench,
  IdentificationCard,
  AddressBook,
  Briefcase,
  Hammer,
  Receipt,
  CreditCard,
  FolderOpen,
  ClockCounterClockwise,
  Tray,
  Eye,
  Tag,
  Check,
} from "@phosphor-icons/react";
import { SegTabs, useSwipeTabs } from "@/components/SegTabs";
import { Button } from "@/components/ui/button";
import { MoveAccountDialog } from "@/components/MoveAccountDialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { AppHeader } from "@/components/AppHeader";
import { AddressCombo } from "@/components/AddressCombo";
import { AssistantDock } from "@/components/AssistantDock";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { StageBadge } from "@/components/StageBadge";
import { CUSTOMER_DOT } from "@/pages/Customers";
import { CustomerProfile } from "@/components/CustomerProfile";
import { FileVault } from "@/components/FileVault";
import { FinanceLedger } from "@/components/FinanceLedger";
import { DateField } from "@/components/DateTimeField";
import { SmsPanel } from "@/components/SmsPanel";
import { PortalInviteCard } from "@/components/PortalInviteCard";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useDirectory } from "@/lib/directory";

const REQUEST_STYLE = {
  New: "border-blue-500/40 bg-blue-500/10 text-blue-300",
  Contacted: "border-indigo-500/40 bg-indigo-500/10 text-indigo-300",
  Quoted: "border-amber-500/40 bg-amber-500/10 text-amber-300",
  Won: "border-emerald-500/40 bg-emerald-500/10 text-emerald-300",
  Lost: "border-red-500/40 bg-red-500/10 text-red-300",
};

const TEMPLATES = [
  ["install_reminder", "Install reminder"],
  ["template_ready", "Templated / in fabrication"],
  ["job_update", "General job update"],
  ["thank_you", "Thank you"],
];

const CTABS = [
  { key: "profile", label: "Profile", Icon: IdentificationCard },
  { key: "contacts", label: "Contacts", Icon: AddressBook },
  { key: "projects", label: "Projects", Icon: Briefcase },
  { key: "jobs", label: "Jobs", Icon: Hammer },
  { key: "quotes", label: "Quotes", Icon: Calculator },
  { key: "finances", label: "Finances", Icon: Receipt },
  { key: "payments", label: "Payments", Icon: CreditCard },
  { key: "files", label: "Files", Icon: FolderOpen },
  { key: "activity", label: "Activity", Icon: ClockCounterClockwise },
  { key: "requests", label: "Requests", Icon: Tray },
];

export default function CustomerDetail() {
  const { id } = useParams();
  const { nameOf } = useDirectory();
  const navigate = useNavigate();
  const [c, setC] = useState(null);
  const [newContact, setNewContact] = useState(null);
  const [tab, setTab] = useState("profile");
  const swipe = useSwipeTabs(CTABS, tab, (t) => setTab(t.key));
  const [jobs, setJobs] = useState([]);
  const [activities, setActivities] = useState([]);
  const [requests, setRequests] = useState([]);
  const [log, setLog] = useState({
    type: "Call",
    summary: "",
    detail: "",
    follow_up_date: "",
  });
  const [template, setTemplate] = useState("install_reminder");
  const [emailJob, setEmailJob] = useState("none");
  const [sending, setSending] = useState(false);
  const [emailDraft, setEmailDraft] = useState(null);
  const [msgTab, setMsgTab] = useState("email");
  const [pos, setPos] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [projects, setProjects] = useState([]);
  const [archivedProjects, setArchivedProjects] = useState([]);
  const [projView, setProjView] = useState("active");
  const [invoices, setInvoices] = useState([]);
  const [reminding, setReminding] = useState(false);

  const [panel, setPanel] = useState(null);
  const [draft, setDraft] = useState({});
  const [delOpen, setDelOpen] = useState(false);
  const [moveAllOpen, setMoveAllOpen] = useState(false);
  const [invitingEmail, setInvitingEmail] = useState("");
  const inviteContact = async (email) => {
    setInvitingEmail(email);
    try {
      const { data } = await api.post(`/customers/${id}/portal-invite`, { email });
      toast.success(`Portal link emailed to ${data.email}`);
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) ||
          "Could not send the invite",
      );
    }
    setInvitingEmail("");
  };
  const initialsOf = (n) =>
    (n || "")
      .trim()
      .split(/\s+/)
      .map((w) => w[0])
      .slice(0, 2)
      .join("")
      .toUpperCase() || "?";
  const [newProject, setNewProject] = useState(null);
  const [creating, setCreating] = useState(false);

  const startNewProject = () => {
    setTab("projects");
    setNewProject({ name: "", job_address: c?.address || "" });
  };

  const createProject = async () => {
    setCreating(true);
    try {
      const { data } = await api.post("/projects", {
        name: newProject.name.trim(),
        customer_id: id,
        job_address: newProject.job_address,
        stage: "Lead",
      });
      toast.success("Project created");
      navigate(`/projects/${data.id}`);
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) ||
          "Could not create the project",
      );
    }
    setCreating(false);
  };

  const saveContacts = async () => {
    try {
      const { data } = await api.patch(`/customers/${id}`, draft);
      setC(data);
      setPanel(null);
      toast.success("Account updated");
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) || "Could not save",
      );
    }
  };

  const startNewContact = () => {
    setTab("contacts");
    setPanel("contacts");
    setDraft({
      contacts: [
        ...(c?.contacts || []).map((x) => ({ ...x })),
        { name: "", role: "", phone: "", mobile: "", email: "", notes: "", primary: false },
      ],
    });
  };

  const load = useCallback(async () => {
    const [cust, jobList, acts, reqs, quoteList, projList, archProjList, invList] =
      await Promise.all([
        api.get(`/customers/${id}`),
        api.get("/jobs", { params: { customer_id: id } }),
        api.get(`/customers/${id}/activities`),
        api.get("/leads", { params: { customer_id: id } }),
        api.get("/quotes", { params: { customer_id: id } }),
        api.get("/projects", { params: { customer_id: id } }),
        api.get("/projects", { params: { customer_id: id, archived: true } }),
        api.get("/invoices", { params: { customer_id: id } }),
      ]);
    setC(cust.data);
    setJobs(jobList.data);
    setActivities(acts.data);
    setRequests(reqs.data || []);
    setQuotes(quoteList.data || []);
    setProjects(projList.data?.projects || []);
    setArchivedProjects(archProjList.data?.projects || []);
    setInvoices(
      Array.isArray(invList.data) ? invList.data : invList.data?.invoices || [],
    );
  }, [id]);

  const mergeProject = async (fromId, intoId) => {
    try {
      await api.post("/projects/merge", { from_id: fromId, into_id: intoId });
      toast.success("Projects merged");
      await load();
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) ||
          "Could not merge the projects",
      );
    }
  };

  useEffect(() => {
    load().catch(() => toast.error("Account not found"));
  }, [load]);

  const addLog = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/customers/${id}/activities`, log);
      setLog({ type: log.type, summary: "", detail: "", follow_up_date: "" });
      toast.success("Activity logged");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  useEffect(() => {
    api
      .get("/purchase-orders", { params: { customer_id: id } })
      .then(({ data }) => setPos(data))
      .catch(() => setPos([]));
  }, [id]);

  const loadDraft = async () => {
    try {
      const { data } = await api.get("/email-templates");
      setEmailDraft({
        subject: data.templates[template].subject,
        body: data.templates[template].body,
      });
    } catch {
      toast.error("Could not load the template");
    }
  };

  const sendEmail = async () => {
    setSending(true);
    try {
      await api.post(`/customers/${id}/email`, {
        template,
        reservation_id: emailJob === "none" ? null : emailJob,
        subject: emailDraft?.subject || "",
        body: emailDraft?.body || "",
      });
      toast.success(`Email sent to ${c.email}`);
      setEmailDraft(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
    setSending(false);
  };

  const remove = async () => {
    await api.delete(`/customers/${id}`);
    toast.success("Account deleted");
    navigate("/customers");
  };

  const sendReminder = async () => {
    setReminding(true);
    try {
      const { data } = await api.post(`/customers/${id}/payment-reminder`);
      toast.success(
        data.sent
          ? `Sent ${data.sent} payment reminder${
              data.sent === 1 ? "" : "s"
            } ($${Math.round(data.balance).toLocaleString()} outstanding)`
          : "No unpaid invoices to remind",
      );
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send reminders");
    } finally {
      setReminding(false);
    }
  };

  if (!c)
    return (
      <div className="grain min-h-screen">
        <AppHeader title="Account" />
        <div className="mx-auto max-w-3xl px-4 pt-10">
          <div className="h-40 animate-pulse rounded-lg border border-zinc-800 bg-zinc-900" />
        </div>
      </div>
    );

  const fmtMoney = (n) => `$${(Number(n) || 0).toFixed(2)}`;
  const quotedTotal = quotes.reduce((s, q) => s + (q.totals?.total || 0), 0);
  const wonQuotes = quotes.filter((q) => q.status === "Accepted");
  const wonTotal = wonQuotes.reduce((s, q) => s + (q.totals?.total || 0), 0);
  const openInvoices = invoices.filter(
    (i) => !["Paid", "Void"].includes(i.status),
  );
  const openBalance = openInvoices.reduce((s, i) => s + (i.balance || 0), 0);
  const credits = Number(c.credits) || 0;

  const fmtDateTime = (d) => {
    if (!d) return "";
    const t = new Date(d);
    if (isNaN(t.getTime())) return String(d).slice(0, 16).replace("T", " ");
    return t.toLocaleString(undefined, {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  };

  const viewAsCustomer = async () => {
    try {
      const { data } = await api.post(`/portal/preview-link/${id}`);
      window.open(data.url, "_blank", "noopener");
      toast.success(`Opening the portal as ${data.email}`);
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) ||
          "Could not open the portal preview",
      );
    }
  };

  const PRICE_MODE_BADGE = {
    on: { label: "Prices: On", cls: "border-emerald-500/40 bg-emerald-500/10 text-emerald-300" },
    off: { label: "Prices: Off", cls: "border-zinc-700 bg-zinc-800/60 text-zinc-400" },
    default: { label: "Prices: Follows global", cls: "border-zinc-700 bg-zinc-900 text-zinc-400" },
  };
  const currentPriceMode = (c.portal_prices || "default").toLowerCase();
  const priceBadge = PRICE_MODE_BADGE[currentPriceMode] || PRICE_MODE_BADGE.default;

  const setPriceMode = async (mode) => {
    if (mode === currentPriceMode) return;
    try {
      const { data } = await api.patch(`/customers/${id}`, { portal_prices: mode });
      setC(data);
      toast.success(`Portal prices set to ${PRICE_MODE_BADGE[mode].label.replace("Prices: ", "")}`);
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) ||
          "Could not update portal prices",
      );
    }
  };

  const PRICE_MODE_OPTIONS = [
    { value: "on", label: "On — show prices" },
    { value: "off", label: "Off — hide prices" },
    { value: "default", label: "Follow global setting" },
  ];

  const actionsMenu = (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          data-testid="customer-actions-menu"
          title="Account actions"
          className="flex h-10 w-10 items-center justify-center rounded-md border border-zinc-800 text-zinc-300 transition-colors duration-150 hover:border-zinc-700"
        >
          <DotsThreeVertical size={20} weight="bold" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        data-testid="customer-actions-menu-content"
        className="w-56 border-zinc-800 bg-zinc-900"
      >
        <DropdownMenuItem
          data-testid="menu-new-job"
          onClick={() => navigate(`/projects/new?customer=${c.id}`)}
        >
          <CalendarPlus size={15} weight="bold" className="mr-2" /> New job
        </DropdownMenuItem>
        <DropdownMenuItem
          data-testid="menu-new-service"
          onClick={() => navigate(`/projects/service/new?customer=${c.id}`)}
        >
          <Wrench size={15} weight="bold" className="mr-2" /> New service
        </DropdownMenuItem>
        <DropdownMenuItem
          data-testid="menu-new-quote"
          onClick={() => navigate(`/quotes/new?customer=${c.id}`)}
        >
          <Calculator size={15} weight="bold" className="mr-2" /> New quote
        </DropdownMenuItem>
        <DropdownMenuItem data-testid="menu-new-project" onClick={startNewProject}>
          <FolderSimplePlus size={15} weight="bold" className="mr-2" /> New project
        </DropdownMenuItem>
        <DropdownMenuItem data-testid="menu-new-contact" onClick={startNewContact}>
          <Plus size={15} weight="bold" className="mr-2" /> New contact
        </DropdownMenuItem>
        <DropdownMenuItem
          data-testid="menu-move-all-account"
          onClick={() => setMoveAllOpen(true)}
        >
          <ArrowsClockwise size={15} weight="bold" className="mr-2" /> Move all to another account
        </DropdownMenuItem>
        <DropdownMenuItem
          data-testid="menu-edit-account"
          onClick={() => navigate(`/customers/${id}/edit`)}
        >
          <PencilSimple size={15} weight="bold" className="mr-2" /> Edit account
        </DropdownMenuItem>
        <DropdownMenuItem data-testid="menu-view-as-customer" onClick={viewAsCustomer}>
          <Eye size={15} weight="bold" className="mr-2" /> View portal as this customer
        </DropdownMenuItem>
        <DropdownMenuSeparator className="bg-zinc-800" />
        <DropdownMenuItem
          data-testid="menu-delete-account"
          onClick={() => setDelOpen(true)}
          className="text-red-400 focus:text-red-300"
        >
          <Trash size={15} weight="bold" className="mr-2" /> Delete account
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );

  return (
    <div className="grain min-h-screen">
      <AppHeader title={c.company || c.name} />
      <AssistantDock
        context={{
          kind: "customer",
          id: id,
          label: c.name,
          prompts: [
            `Add a reminder task to follow up with ${c.name}`,
            `Update ${c.name}'s phone to `,
            `Email ${c.name} to `,
          ],
        }}
      />
      <main className="relative z-10 mx-auto max-w-[1400px] px-4 pb-24 pt-6">
        {/* HEADER */}
        <div
          className="flex flex-wrap items-start justify-between gap-4"
          data-testid="customer-header-card"
        >
          <div className="flex min-w-0 items-start gap-3">
            <button
              onClick={() => navigate("/customers")}
              data-testid="back-button"
              title="All accounts"
              className="mt-1 text-zinc-400 transition-colors duration-150 hover:text-white"
            >
              <ArrowLeft size={20} weight="bold" />
            </button>
            <div className="min-w-0">
              <div className="flex items-center gap-3 min-w-0">
                <h1
                  className="min-w-0 truncate text-2xl font-bold tracking-tight"
                  data-testid="customer-detail-name"
                >
                  {c.company || c.name}
                </h1>
                <span
                  data-testid="customer-detail-status"
                  className="inline-flex shrink-0 items-center gap-2 text-xs font-semibold uppercase tracking-wider text-zinc-200"
                >
                  <span
                    className={`h-2.5 w-2.5 shrink-0 rounded-full ${
                      CUSTOMER_DOT[c.status] || CUSTOMER_DOT.Lead
                    }`}
                  />
                  {c.status}
                </span>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      type="button"
                      data-testid="customer-price-mode-badge"
                      title="Click to turn portal prices on or off for this account"
                      className={`inline-flex shrink-0 items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider transition-colors duration-150 hover:brightness-110 ${priceBadge.cls}`}
                    >
                      <Tag size={11} weight="bold" />
                      {priceBadge.label}
                      <CaretDown size={10} weight="bold" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    align="start"
                    data-testid="customer-price-mode-menu"
                    className="w-52 border-zinc-800 bg-zinc-900"
                  >
                    {PRICE_MODE_OPTIONS.map((opt) => (
                      <DropdownMenuItem
                        key={opt.value}
                        data-testid={`price-mode-${opt.value}`}
                        onClick={() => setPriceMode(opt.value)}
                        className="flex items-center justify-between"
                      >
                        {opt.label}
                        {currentPriceMode === opt.value && (
                          <Check size={14} weight="bold" className="text-emerald-400" />
                        )}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
              {c.company && c.name && (
                <p
                  className="mt-1 text-sm font-medium text-zinc-300"
                  data-testid="customer-contact-name"
                >
                  Attn: {c.name}
                </p>
              )}
              <p
                className="mt-1 text-xs text-zinc-500"
                data-testid="customer-meta"
              >
                Created: {fmtDateTime(c.created_at)}
                {c.created_by ? ` by ${nameOf(c.created_by)}` : ""}
                {c.updated_at ? (
                  <>
                    {" · "}Last Modified: {fmtDateTime(c.updated_at)}
                    {c.updated_by ? ` by ${nameOf(c.updated_by)}` : ""}
                  </>
                ) : null}
              </p>
            </div>
          </div>
          <div className="flex items-start gap-5">
            <button
              type="button"
              data-testid="ar-balance-open"
              onClick={() => setTab("payments")}
              className="text-right"
              title="View payments"
            >
              <div className="flex items-center justify-end gap-1 text-[10px] font-bold uppercase tracking-widest text-zinc-500">
                AR Balance <CaretDown size={10} weight="bold" />
              </div>
              <div
                data-testid="ar-balance"
                className={`mt-0.5 text-lg font-bold ${
                  openBalance > 0 ? "text-amber-300" : "text-zinc-200"
                }`}
              >
                {fmtMoney(openBalance)}
              </div>
            </button>
            <div className="text-right">
              <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">
                Credits
              </div>
              <div
                data-testid="credits-balance"
                className="mt-0.5 text-lg font-bold text-zinc-200"
              >
                {fmtMoney(credits)}
              </div>
            </div>
            {actionsMenu}
          </div>
        </div>

        {/* TABS */}
        <SegTabs
          sticky
          className="mt-5"
          testId="customer-tabs"
          tabs={CTABS}
          value={tab}
          onChange={(t) => setTab(t.key)}
        />

        <div className="mt-5" data-testid={`customer-panel-${tab}`} {...swipe}>
          {tab === "profile" && (
            <CustomerProfile c={c} id={id} onSaved={(data) => setC(data)} />
          )}

          {tab === "contacts" && (
            <div
              className="rounded-lg border border-zinc-800 bg-zinc-900 p-4"
              id="contacts-card"
            >
              <div className="flex items-center justify-between">
                <div className="text-xs font-bold uppercase tracking-widest text-zinc-300">
                  Contacts
                </div>
                {panel === "contacts" ? (
                  <span className="flex gap-2">
                    <button
                      data-testid="contacts-save"
                      onClick={saveContacts}
                      className="inline-flex h-8 items-center gap-1 rounded-md bg-indigo-500 px-3 text-[11px] font-bold uppercase tracking-widest text-white hover:bg-indigo-400"
                    >
                      Save
                    </button>
                    <button
                      data-testid="contacts-cancel"
                      onClick={() => setPanel(null)}
                      className="inline-flex h-8 items-center rounded-md border border-zinc-700 px-3 text-[11px] uppercase tracking-widest text-zinc-400 hover:bg-zinc-800"
                    >
                      Cancel
                    </button>
                  </span>
                ) : (
                  <button
                    data-testid="contacts-edit"
                    onClick={() => {
                      setPanel("contacts");
                      setDraft({
                        contacts: (c.contacts || []).map((x) => ({ ...x })),
                      });
                    }}
                    className="text-zinc-500 transition-colors duration-150 hover:text-indigo-300"
                  >
                    <PencilSimple size={15} weight="bold" />
                  </button>
                )}
              </div>
              {panel === "contacts" ? (
                <div className="mt-3 space-y-3" data-testid="contacts-edit-list">
                  {draft.contacts.length === 0 && (
                    <p className="rounded-lg border border-dashed border-zinc-700 bg-zinc-950/40 px-4 py-6 text-center text-sm text-zinc-500">
                      No contacts yet — add the people you deal with on this account.
                    </p>
                  )}
                  {draft.contacts.map((ct, i) => (
                    <div
                      key={i}
                      data-testid={`acct-contact-card-${i}`}
                      className="flex items-center justify-between gap-3 rounded-xl border border-zinc-800 bg-zinc-950/60 p-3"
                    >
                      <button
                        type="button"
                        data-testid={`acct-contact-${i}-edit`}
                        onClick={() => setNewContact({ ...ct, _idx: i })}
                        className="flex min-w-0 flex-1 items-center gap-3 text-left"
                      >
                        <span
                          data-testid={`acct-contact-${i}-avatar`}
                          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-indigo-500/30 bg-indigo-500/15 text-xs font-bold text-indigo-200"
                        >
                          {initialsOf(ct.name)}
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-sm font-semibold text-zinc-100">
                            {ct.name || "Unnamed contact"}
                          </span>
                          <span className="block truncate text-xs text-zinc-500">
                            {[ct.role, ct.email, ct.phone].filter(Boolean).join(" · ") ||
                              "Tap to edit"}
                          </span>
                        </span>
                        {ct.primary && (
                          <span className="inline-flex shrink-0 items-center gap-1 rounded-full border border-amber-400/50 bg-amber-400/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-amber-200">
                            <Star size={11} weight="fill" /> Primary
                          </span>
                        )}
                        {ct.private && (
                          <span className="inline-flex shrink-0 items-center rounded-full border border-rose-400/50 bg-rose-400/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-rose-200">
                            Private
                          </span>
                        )}
                      </button>
                      <button
                        data-testid={`acct-remove-contact-${i}`}
                        onClick={() =>
                          setDraft({
                            ...draft,
                            contacts: draft.contacts.filter((_, j) => j !== i),
                          })
                        }
                        className="shrink-0 rounded p-1.5 text-zinc-500 hover:bg-red-950/40 hover:text-red-400"
                        aria-label="Remove contact"
                      >
                        <X size={14} weight="bold" />
                      </button>
                    </div>
                  ))}
                  <button
                    data-testid="acct-add-contact"
                    onClick={() =>
                      setNewContact({
                        name: "",
                        role: "",
                        phone: "",
                        mobile: "",
                        email: "",
                        notes: "",
                        primary: false,
                        private: false,
                        _idx: null,
                      })
                    }
                    className="inline-flex items-center gap-1.5 rounded-lg border border-dashed border-zinc-700 px-3 py-2 text-[11px] font-bold uppercase tracking-widest text-indigo-300 hover:border-indigo-500 hover:bg-indigo-500/5"
                  >
                    <Plus size={13} weight="bold" /> Add contact
                  </button>
                </div>
              ) : (
                <div className="mt-3 space-y-2" data-testid="customer-contacts">
                  {(c.contacts || []).length === 0 && (
                    <p className="text-sm text-zinc-500">No contacts yet.</p>
                  )}
                  {(c.contacts || []).map((ct, i) => (
                    <div
                      key={i}
                      data-testid={`customer-contact-${i}`}
                      className="rounded-xl border border-zinc-800 bg-zinc-950/40 p-4"
                    >
                      <div className="flex flex-wrap items-center gap-2">
                        <span
                          data-testid={`customer-contact-${i}-avatar`}
                          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-indigo-500/30 bg-indigo-500/15 text-xs font-bold text-indigo-200"
                        >
                          {initialsOf(ct.name)}
                        </span>
                        {ct.primary && (
                          <span className="inline-flex items-center gap-1 rounded-full border border-amber-400/50 bg-amber-400/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-amber-200">
                            <Star size={11} weight="fill" /> Primary
                          </span>
                        )}
                        {ct.private && (
                          <span className="rounded-full border border-rose-400/50 bg-rose-400/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-rose-200">
                            Private
                          </span>
                        )}
                        <span className="text-sm font-semibold">
                          {ct.name || "—"}
                        </span>
                        {ct.role && (
                          <span className="rounded-full border border-zinc-700 px-2 py-0.5 text-[10px] uppercase tracking-widest text-zinc-400">
                            {ct.role}
                          </span>
                        )}
                      </div>
                      <div className="mt-2 flex flex-wrap gap-2 text-sm text-zinc-400">
                        {ct.phone && (
                          <a
                            href={`tel:${ct.phone}`}
                            className="inline-flex items-center gap-2 rounded-full border border-zinc-800 bg-zinc-950 px-3 py-1 text-xs transition-colors duration-150 hover:border-indigo-500 hover:text-indigo-300"
                          >
                            <Phone size={13} weight="bold" /> {ct.phone}
                          </a>
                        )}
                        {ct.mobile && (
                          <a
                            href={`tel:${ct.mobile}`}
                            className="inline-flex items-center gap-2 rounded-full border border-zinc-800 bg-zinc-950 px-3 py-1 text-xs transition-colors duration-150 hover:border-indigo-500 hover:text-indigo-300"
                          >
                            <DeviceMobile size={13} weight="bold" /> {ct.mobile}
                          </a>
                        )}
                        {ct.email && (
                          <a
                            href={`mailto:${ct.email}`}
                            className="inline-flex items-center gap-2 rounded-full border border-zinc-800 bg-zinc-950 px-3 py-1 text-xs transition-colors duration-150 hover:border-indigo-500 hover:text-indigo-300"
                          >
                            <EnvelopeSimple size={14} weight="bold" /> {ct.email}
                          </a>
                        )}
                        {ct.email && (
                          <button
                            type="button"
                            data-testid={`customer-contact-${i}-invite`}
                            disabled={invitingEmail === ct.email}
                            onClick={() => inviteContact(ct.email)}
                            className="inline-flex items-center gap-2 rounded-full border border-[#c39e6d]/50 bg-[#c39e6d]/10 px-3 py-1 text-xs font-semibold text-[#c39e6d] transition-colors duration-150 hover:bg-[#c39e6d]/20 disabled:opacity-50"
                          >
                            <PaperPlaneTilt size={13} weight="bold" />
                            {invitingEmail === ct.email ? "Sending…" : "Invite to portal"}
                          </button>
                        )}
                      </div>
                      {ct.notes && (
                        <p className="mt-2 text-xs text-zinc-500">{ct.notes}</p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {tab === "projects" && (
            <div className="rounded-lg border border-zinc-800 bg-zinc-900 p-4">
              <div className="flex items-center justify-between gap-3">
                <div className="text-xs font-bold uppercase tracking-widest text-zinc-300">
                  Projects
                </div>
                <div className="flex items-center gap-3">
                  <div className="inline-flex rounded-full border border-zinc-800 bg-zinc-950 p-0.5" data-testid="customer-project-filter">
                    {[
                      ["active", "Active", projects.length],
                      ["archived", "Archived", archivedProjects.length],
                    ].map(([v, l, n]) => (
                      <button
                        key={v}
                        type="button"
                        data-testid={`customer-project-filter-${v}`}
                        onClick={() => setProjView(v)}
                        className={`rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-widest transition-colors duration-150 ${
                          projView === v ? "bg-zinc-200 text-zinc-900" : "text-zinc-400 hover:text-zinc-200"
                        }`}
                      >
                        {l} · {n}
                      </button>
                    ))}
                  </div>
                  <Button
                    data-testid="customer-new-project"
                    variant="outline"
                    onClick={startNewProject}
                    className="h-8 border-zinc-700 bg-transparent text-[10px] font-bold uppercase tracking-widest hover:bg-zinc-800"
                  >
                    <Plus size={12} weight="bold" className="mr-1" /> New project
                  </Button>
                </div>
              </div>
              {newProject && (
                <div
                  className="mt-3 grid gap-3 rounded-lg border border-amber-400/30 bg-amber-400/[0.06] p-3 md:grid-cols-[1.2fr_1.4fr_auto]"
                  data-testid="customer-new-project-form"
                >
                  <Input
                    data-testid="customer-new-project-name"
                    autoFocus
                    value={newProject.name}
                    onChange={(e) =>
                      setNewProject({ ...newProject, name: e.target.value })
                    }
                    onKeyDown={(e) =>
                      e.key === "Enter" && newProject.name.trim() && createProject()
                    }
                    placeholder="Project name — e.g. Smith kitchen"
                    className="h-10 border-zinc-800 bg-zinc-950"
                  />
                  <AddressCombo
                    testId="customer-new-project-address"
                    value={newProject.job_address}
                    onChange={(v) => setNewProject({ ...newProject, job_address: v })}
                    options={[c?.address, ...(c?.job_addresses || [])]}
                    placeholder="Job address"
                  />
                  <div className="flex gap-2">
                    <Button
                      data-testid="customer-new-project-save"
                      disabled={creating || !newProject.name.trim()}
                      onClick={createProject}
                      className="h-10 bg-indigo-500 text-[10px] font-bold uppercase tracking-widest text-white hover:bg-indigo-400"
                    >
                      Create
                    </Button>
                    <Button
                      data-testid="customer-new-project-cancel"
                      variant="outline"
                      onClick={() => setNewProject(null)}
                      className="h-10 border-zinc-800 bg-zinc-950 text-[10px] font-bold uppercase tracking-widest"
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              )}
              <div className="mt-3 space-y-2" data-testid="customer-projects">
                {(() => {
                  const shown = projView === "archived" ? archivedProjects : projects;
                  const norm = (s) =>
                    (s || "").toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
                  const dupKey = (p) => `${norm(p.name)}|${norm(p.job_address)}`;
                  const groups = {};
                  shown.forEach((p) => {
                    (groups[dupKey(p)] = groups[dupKey(p)] || []).push(p);
                  });
                  return (
                    <>
                      {shown.length === 0 && (
                        <p className="text-sm text-zinc-500">
                          {projView === "archived"
                            ? "No completed or archived projects yet."
                            : "No projects yet — start one with New project, or let quotes and jobs group into projects automatically."}
                        </p>
                      )}
                      {shown.map((p) => {
                        const qcodes = (p.quotes || [])
                          .map((q) => q.code)
                          .filter(Boolean);
                        const dups = (groups[dupKey(p)] || []).filter(
                          (x) => x.id !== p.id,
                        );
                        return (
                          <div
                            key={p.id}
                            data-testid={`customer-project-${p.id}`}
                            className="flex items-center justify-between gap-3 rounded-md border border-zinc-800 p-3 transition-colors duration-150 hover:border-indigo-500/40 hover:bg-zinc-800"
                          >
                            <Link to={`/projects/${p.id}`} className="min-w-0 flex-1">
                              <div className="flex flex-wrap items-center gap-1.5">
                                <span className="font-mono-data text-xs text-indigo-300">
                                  {p.code}
                                </span>
                                {qcodes.map((qc) => (
                                  <span
                                    key={qc}
                                    data-testid={`customer-project-${p.id}-quote-${qc}`}
                                    className="font-mono-data rounded border border-zinc-700 bg-zinc-900 px-1.5 py-0.5 text-[10px] text-zinc-400"
                                  >
                                    {qc}
                                  </span>
                                ))}
                              </div>
                              <div className="truncate text-sm font-semibold">
                                {p.name}
                              </div>
                              <div className="text-xs text-zinc-500">
                                {p.job_address || "no address"}
                              </div>
                            </Link>
                            <div className="flex shrink-0 items-center gap-2">
                              <StageBadge stage={p.stage} />
                              {dups.length > 0 && (
                                <DropdownMenu>
                                  <DropdownMenuTrigger
                                    data-testid={`customer-project-${p.id}-merge`}
                                    onClick={(e) => e.stopPropagation()}
                                    className="rounded-md border border-amber-500/40 bg-amber-500/10 px-2 py-1 text-[10px] font-bold uppercase tracking-widest text-amber-300 transition-colors hover:bg-amber-500/20"
                                  >
                                    Merge
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent align="end">
                                    <div className="px-2 py-1.5 text-[10px] uppercase tracking-widest text-zinc-500">
                                      Merge {p.code} into…
                                    </div>
                                    {dups.map((d) => (
                                      <DropdownMenuItem
                                        key={d.id}
                                        data-testid={`customer-project-${p.id}-merge-into-${d.id}`}
                                        onClick={() => mergeProject(p.id, d.id)}
                                      >
                                        {d.code} · {d.name}
                                      </DropdownMenuItem>
                                    ))}
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </>
                  );
                })()}
              </div>
            </div>
          )}

          {tab === "jobs" && (
            <div className="rounded-lg border border-zinc-800 bg-zinc-900 p-4">
              <div className="flex items-center justify-between">
                <div className="text-xs font-bold uppercase tracking-widest text-zinc-300">
                  Jobs
                </div>
                <span className="font-mono-data text-xs text-zinc-500">
                  {jobs.length}
                </span>
              </div>
              <div className="mt-3 space-y-2" data-testid="customer-jobs">
                {jobs.length === 0 && (
                  <p className="text-sm text-zinc-500">
                    No jobs yet for this account.
                  </p>
                )}
                {jobs.map((j) => (
                  <Link
                    key={j.id}
                    to={`/projects/${j.id}?tab=field`}
                    data-testid={`customer-job-${j.id}`}
                    className="flex items-center justify-between gap-3 rounded-md border border-zinc-800 p-3 transition-colors duration-150 hover:border-indigo-500/40 hover:bg-zinc-800"
                  >
                    <div className="min-w-0">
                      <div className="font-mono-data text-xs text-indigo-300">
                        {j.code}
                      </div>
                      <div className="truncate text-sm font-semibold">
                        {j.project_name}
                      </div>
                      <div className="truncate text-xs text-zinc-500">
                        {j.address || "no address"}
                        {j.install_date ? ` · Install ${j.install_date}` : ""}
                      </div>
                    </div>
                    <StageBadge stage={j.stage} />
                  </Link>
                ))}
              </div>
            </div>
          )}

          {tab === "quotes" && (
            <div className="rounded-lg border border-zinc-800 bg-zinc-900 p-4">
              <div className="flex items-center justify-between gap-3">
                <div className="text-xs font-bold uppercase tracking-widest text-zinc-300">
                  Quotes
                </div>
                <div className="flex items-center gap-3">
                  <span className="font-mono-data text-xs text-zinc-500">
                    {quotes.length}
                  </span>
                  <Button
                    data-testid="customer-new-quote"
                    variant="outline"
                    onClick={() => navigate(`/quotes/new?customer=${c.id}`)}
                    className="h-8 border-zinc-700 bg-transparent text-[10px] font-bold uppercase tracking-widest hover:bg-zinc-800"
                  >
                    <Plus size={12} weight="bold" className="mr-1" /> New quote
                  </Button>
                </div>
              </div>
              <div className="mt-3 space-y-2" data-testid="customer-quotes">
                {quotes.length === 0 && (
                  <p className="text-sm text-zinc-500">
                    No quotes yet — use New quote to create one.
                  </p>
                )}
                {quotes.map((qt) => {
                  const proj = projects.find((p) => p.id === qt.project_id);
                  const job = jobs.find((j) => j.id === qt.job_id);
                  return (
                    <Link
                      key={qt.id}
                      to={`/quotes/${qt.id}`}
                      data-testid={`customer-quote-${qt.id}`}
                      className="flex items-center justify-between gap-3 rounded-md border border-zinc-800 p-3 transition-colors duration-150 hover:border-indigo-500/40 hover:bg-zinc-800"
                    >
                      <div className="min-w-0">
                        <div className="font-mono-data text-xs text-indigo-300">
                          {qt.code}
                        </div>
                        <div className="truncate text-sm font-semibold">
                          {qt.project_name || "Untitled quote"}
                        </div>
                        <div className="text-xs text-zinc-500">
                          {qt.slab_color || "—"}
                        </div>
                        {(proj || job) && (
                          <div
                            className="mt-1.5 flex flex-wrap items-center gap-1.5"
                            data-testid={`customer-quote-${qt.id}-trace`}
                          >
                            <span className="text-[10px] uppercase tracking-widest text-zinc-600">
                              Became
                            </span>
                            {proj && (
                              <span className="rounded border border-indigo-500/40 bg-indigo-500/10 px-1.5 py-0.5 text-[10px] font-semibold text-indigo-300">
                                {proj.code}
                              </span>
                            )}
                            {job && (
                              <span className="rounded border border-zinc-700 px-1.5 py-0.5 text-[10px] font-semibold text-zinc-300">
                                {job.code}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                      <div className="flex shrink-0 flex-col items-end gap-1">
                        <span
                          data-testid={`customer-quote-${qt.id}-total`}
                          className="font-mono-data text-sm font-bold text-zinc-100"
                        >
                          {fmtMoney(qt.totals?.total)}
                        </span>
                        <span
                          data-testid={`customer-quote-${qt.id}-status`}
                          className={`rounded-full border px-2.5 py-0.5 text-[11px] font-semibold ${
                            qt.status === "Accepted"
                              ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-300"
                              : qt.status === "Expired" || qt.status === "Declined"
                                ? "border-red-500/40 bg-red-500/10 text-red-300"
                                : qt.status === "Sent"
                                  ? "border-sky-500/40 bg-sky-500/10 text-sky-300"
                                  : "border-zinc-700 text-zinc-300"
                          }`}
                        >
                          {qt.status || "Draft"}
                        </span>
                      </div>
                    </Link>
                  );
                })}
              </div>
            </div>
          )}

          {tab === "finances" && (
            <div className="space-y-4">
              <div
                className="grid grid-cols-1 gap-3 sm:grid-cols-3"
                data-testid="customer-totals"
              >
                <div className="rounded-lg border border-zinc-800 bg-zinc-900 p-4">
                  <div className="text-xs uppercase tracking-widest text-zinc-500">
                    Quoted
                  </div>
                  <div
                    className="mt-1 text-2xl font-bold tracking-tight"
                    data-testid="total-quoted"
                  >
                    {fmtMoney(quotedTotal)}
                  </div>
                  <div className="mt-0.5 text-xs text-zinc-500">
                    {quotes.length} quote{quotes.length === 1 ? "" : "s"}
                  </div>
                </div>
                <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-4">
                  <div className="text-xs uppercase tracking-widest text-emerald-400/80">
                    Won
                  </div>
                  <div
                    className="mt-1 text-2xl font-bold tracking-tight text-emerald-300"
                    data-testid="total-won"
                  >
                    {fmtMoney(wonTotal)}
                  </div>
                  <div className="mt-0.5 text-xs text-zinc-500">
                    {wonQuotes.length} accepted
                  </div>
                </div>
                <div
                  className={`rounded-lg border p-4 ${
                    openBalance > 0
                      ? "border-amber-500/40 bg-amber-500/5"
                      : "border-zinc-800 bg-zinc-900"
                  }`}
                >
                  <div className="text-xs uppercase tracking-widest text-zinc-500">
                    Open balance
                  </div>
                  <div
                    className={`mt-1 text-2xl font-bold tracking-tight ${
                      openBalance > 0 ? "text-amber-300" : "text-zinc-200"
                    }`}
                    data-testid="total-open-balance"
                  >
                    {fmtMoney(openBalance)}
                  </div>
                  <div className="mt-0.5 text-xs text-zinc-500">
                    {openInvoices.length} unpaid invoice
                    {openInvoices.length === 1 ? "" : "s"}
                  </div>
                </div>
              </div>

              <FinanceLedger scope="customers" id={id} customerId={id} onChanged={load} />

              <div className="rounded-lg border border-zinc-800 bg-zinc-900 p-4">
                <div className="flex items-center justify-between">
                  <div className="text-xs font-bold uppercase tracking-widest text-zinc-300">
                    Purchase Orders
                  </div>
                  <span className="font-mono-data text-xs text-zinc-500">
                    {pos.length}
                  </span>
                </div>
                <div className="mt-3 space-y-2" data-testid="customer-pos">
                  {pos.length === 0 && (
                    <p className="text-sm text-zinc-500">
                      No purchase orders reference this account yet.
                    </p>
                  )}
                  {pos.map((po) => (
                    <Link
                      key={po.id}
                      to={`/inventory/purchase-orders/${po.id}`}
                      data-testid={`customer-po-${po.id}`}
                      className="flex items-center justify-between gap-3 rounded-md border border-zinc-800 p-3 transition-colors duration-150 hover:border-indigo-500/40 hover:bg-zinc-800"
                    >
                      <div className="min-w-0">
                        <div className="font-mono-data text-xs text-indigo-300">
                          {po.code}
                        </div>
                        <div className="truncate text-sm font-semibold">
                          {po.supplier_name || "No supplier"}
                        </div>
                        <div className="text-xs text-zinc-500">
                          {po.lines.length} line{po.lines.length === 1 ? "" : "s"}
                          {po.expected_date ? ` · due ${po.expected_date}` : ""}
                        </div>
                      </div>
                      <span className="shrink-0 rounded-full border border-zinc-700 px-2 py-0.5 text-[10px] uppercase tracking-widest text-zinc-300">
                        {po.status}
                      </span>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          )}

          {tab === "payments" && (
            <div className="rounded-lg border border-zinc-800 bg-zinc-900 p-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="text-xs font-bold uppercase tracking-widest text-zinc-300">
                  Invoices &amp; Payments
                </div>
                {openBalance > 0 && (
                  <button
                    type="button"
                    data-testid="send-payment-reminder"
                    onClick={sendReminder}
                    disabled={reminding}
                    className="inline-flex items-center gap-1.5 rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-1.5 text-[11px] font-bold uppercase tracking-widest text-amber-300 transition-colors duration-150 hover:bg-amber-500/20 disabled:opacity-50"
                  >
                    <PaperPlaneTilt size={13} weight="bold" />
                    {reminding ? "Sending…" : "Send payment reminder"}
                  </button>
                )}
              </div>
              <div className="mt-3 space-y-2" data-testid="customer-invoices">
                {invoices.length === 0 && (
                  <p className="text-sm text-zinc-500">
                    No invoices yet — accepting a quote raises deposit and
                    balance invoices automatically.
                  </p>
                )}
                {invoices.map((i) => (
                  <Link
                    key={i.id}
                    to={`/invoices/${i.id}`}
                    data-testid={`customer-invoice-${i.id}`}
                    className="flex flex-wrap items-center gap-3 rounded-md border border-zinc-800 p-3 transition-colors duration-150 hover:border-indigo-500/40 hover:bg-zinc-800"
                  >
                    <span className="font-mono-data text-xs text-indigo-300">
                      {i.code}
                    </span>
                    <span className="min-w-0 flex-1 truncate text-sm">
                      {i.title}
                    </span>
                    <span className="text-[10px] font-bold uppercase tracking-widest text-zinc-400">
                      {i.status}
                    </span>
                    <span className="font-mono-data text-sm font-bold">
                      {fmtMoney(i.balance)}
                    </span>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {tab === "files" && (
            <FileVault entity="customers" entityId={id} title="Files" />
          )}

          {tab === "activity" && (
            <div className="space-y-6">
              <div
                className="rounded-lg border border-zinc-800 bg-zinc-900 p-4"
                data-testid="customer-messages-card"
              >
                <div className="flex overflow-hidden rounded-md border border-zinc-800">
                  {[
                    ["email", "Send Email"],
                    ["text", "Texts"],
                    ["call", "Log Call"],
                  ].map(([k, label]) => (
                    <button
                      key={k}
                      data-testid={`messages-tab-${k}`}
                      onClick={() => setMsgTab(k)}
                      className={`h-11 flex-1 text-[11px] font-semibold uppercase tracking-widest transition-colors duration-150 ${
                        msgTab === k
                          ? "bg-indigo-500 text-white"
                          : "bg-zinc-950 text-zinc-400 hover:bg-zinc-800"
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
                {msgTab === "email" ? (
                  <div className="mt-3 space-y-3">
                    <Select value={template} onValueChange={setTemplate}>
                      <SelectTrigger
                        data-testid="email-template-select"
                        className="h-12 border-zinc-800 bg-zinc-950"
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="border-zinc-800 bg-zinc-900">
                        {TEMPLATES.map(([v, label]) => (
                          <SelectItem key={v} value={v} data-testid={`email-template-${v}`}>
                            {label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Select value={emailJob} onValueChange={setEmailJob}>
                      <SelectTrigger
                        data-testid="email-job-select"
                        className="h-12 border-zinc-800 bg-zinc-950"
                      >
                        <SelectValue placeholder="Reference a job (optional)" />
                      </SelectTrigger>
                      <SelectContent className="border-zinc-800 bg-zinc-900">
                        <SelectItem value="none">No job reference</SelectItem>
                        {jobs.map((j) => (
                          <SelectItem key={j.id} value={j.id}>
                            {j.tag} — {j.project_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {emailDraft ? (
                      <div className="space-y-2 rounded-md border border-zinc-800 bg-zinc-950 p-3">
                        <Input
                          data-testid="email-subject-input"
                          value={emailDraft.subject}
                          onChange={(e) =>
                            setEmailDraft({ ...emailDraft, subject: e.target.value })
                          }
                          placeholder="Subject"
                          className="h-11 border-zinc-800 bg-zinc-900"
                        />
                        <Textarea
                          data-testid="email-body-input"
                          rows={6}
                          value={emailDraft.body}
                          onChange={(e) =>
                            setEmailDraft({ ...emailDraft, body: e.target.value })
                          }
                          className="border-zinc-800 bg-zinc-900"
                        />
                        <div className="flex items-center justify-between">
                          <span className="text-[10px] uppercase tracking-widest text-zinc-600">
                            Tags like {"{customer_name}"} are filled in on send
                          </span>
                          <button
                            data-testid="email-discard-edit"
                            onClick={() => setEmailDraft(null)}
                            className="text-[10px] uppercase tracking-widest text-zinc-500 hover:text-zinc-200"
                          >
                            Use saved template
                          </button>
                        </div>
                      </div>
                    ) : (
                      <button
                        data-testid="email-edit-toggle"
                        onClick={loadDraft}
                        className="text-[11px] uppercase tracking-widest text-indigo-300 hover:underline"
                      >
                        Edit wording before sending
                      </button>
                    )}
                    <Button
                      data-testid="send-email-button"
                      variant="outline"
                      onClick={sendEmail}
                      disabled={sending || !c.email}
                      className="h-12 w-full border-zinc-700 bg-transparent text-xs font-bold uppercase tracking-widest text-zinc-200 hover:bg-zinc-800"
                    >
                      <PaperPlaneTilt size={18} weight="bold" className="mr-2" />
                      {sending
                        ? "Sending…"
                        : c.email
                          ? "Send Email"
                          : "No email on file"}
                    </Button>
                  </div>
                ) : msgTab === "call" ? (
                  <div className="mt-3" data-testid="log-call-tab">
                    <form onSubmit={addLog} data-testid="log-activity-form">
                      <div className="mt-3 space-y-3">
                        <Select
                          value={log.type}
                          onValueChange={(v) => setLog({ ...log, type: v })}
                        >
                          <SelectTrigger
                            data-testid="activity-type-select"
                            className="h-12 border-zinc-800 bg-zinc-950"
                          >
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="border-zinc-800 bg-zinc-900">
                            {["Call", "Text", "Email", "Meeting", "Note"].map((t) => (
                              <SelectItem key={t} value={t} data-testid={`activity-type-${t}`}>
                                {t}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <div className="space-y-2">
                          <Label className="text-xs uppercase tracking-widest text-zinc-500">
                            Summary
                          </Label>
                          <Input
                            data-testid="activity-summary-input"
                            required
                            value={log.summary}
                            onChange={(e) =>
                              setLog({ ...log, summary: e.target.value })
                            }
                            placeholder="Confirmed template appointment"
                            className="h-12 border-zinc-800 bg-zinc-950"
                          />
                        </div>
                        <Textarea
                          data-testid="activity-detail-input"
                          value={log.detail}
                          onChange={(e) => setLog({ ...log, detail: e.target.value })}
                          rows={3}
                          placeholder="Details of the conversation…"
                          className="border-zinc-800 bg-zinc-950"
                        />
                        <div className="space-y-2">
                          <Label className="text-xs uppercase tracking-widest text-zinc-500">
                            Follow-up reminder (optional)
                          </Label>
                          <DateField
                            testId="activity-followup-date"
                            value={log.follow_up_date}
                            onChange={(v) => setLog({ ...log, follow_up_date: v })}
                            placeholder="No follow-up"
                          />
                        </div>
                        <Button
                          data-testid="add-activity-button"
                          type="submit"
                          variant="outline"
                          className="h-12 w-full border-zinc-700 bg-transparent font-semibold uppercase tracking-wider hover:bg-zinc-800"
                        >
                          Add To Timeline
                        </Button>
                      </div>
                    </form>
                  </div>
                ) : (
                  <div className="mt-3 [&>div]:border-0 [&>div]:bg-transparent [&>div]:p-0">
                    <SmsPanel
                      entity="customer"
                      entityId={id}
                      defaultTo={c.phone || ""}
                      presets={[
                        {
                          label: "Appointment",
                          text: `Hi ${c.name?.split(" ")[0] || "there"}, this is Brioni Granite confirming your upcoming appointment. Reply here if you need to change the time.`,
                        },
                        {
                          label: "On the way",
                          text: "Hi, our crew is on the way to you now. See you shortly — Brioni Granite.",
                        },
                        {
                          label: "Ready",
                          text: "Good news — your countertops are ready. Reply here and we'll schedule the install.",
                        },
                      ]}
                    />
                  </div>
                )}
              </div>

              <PortalInviteCard customerId={id} email={c.email} />

              <div className="rounded-lg border border-zinc-800 bg-zinc-900 p-4">
                <div className="text-xs font-bold uppercase tracking-widest text-zinc-300">
                  Timeline
                </div>
                <div className="mt-4 space-y-4" data-testid="activity-timeline">
                  {activities.length === 0 && (
                    <p className="text-sm text-zinc-500">
                      No communication logged yet.
                    </p>
                  )}
                  {activities.map((a) => (
                    <div
                      key={a.id}
                      data-testid={`activity-${a.id}`}
                      className="relative rounded-lg border border-zinc-800 bg-zinc-950 p-3 pl-5 transition-colors duration-150 hover:border-indigo-500/40"
                    >
                      <span className="absolute left-0 top-3 h-[calc(100%-1.5rem)] w-1 rounded-full bg-indigo-500/50" />
                      <div className="flex items-center gap-2">
                        <span className="rounded-full border border-indigo-400/30 bg-indigo-500/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-widest text-indigo-300">
                          {a.type}
                        </span>
                        <span className="font-mono-data text-xs text-zinc-500">
                          {(a.occurred_at || a.created_at || "")
                            .slice(0, 16)
                            .replace("T", " ")}
                        </span>
                      </div>
                      <div className="mt-1 text-sm font-semibold">{a.summary}</div>
                      {a.detail && (
                        <p className="mt-1 whitespace-pre-wrap text-sm text-zinc-400">
                          {a.detail}
                        </p>
                      )}
                      {a.follow_up_date && (
                        <button
                          data-testid={`activity-followup-${a.id}`}
                          onClick={async () => {
                            await api.patch(
                              `/customers/${id}/activities/${a.id}/follow-up`,
                              { follow_up_done: !a.follow_up_done },
                            );
                            load();
                          }}
                          className={`mt-2 inline-flex items-center gap-2 rounded-full border px-3 py-1 text-[11px] font-semibold uppercase tracking-widest transition-colors duration-150 ${
                            a.follow_up_done
                              ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-300"
                              : a.follow_up_date <=
                                  new Date().toISOString().slice(0, 10)
                                ? "border-red-500/50 bg-red-500/10 text-red-300"
                                : "border-yellow-500/40 bg-yellow-500/10 text-yellow-300"
                          }`}
                        >
                          {a.follow_up_done
                            ? "Followed up"
                            : `Follow up ${a.follow_up_date}`}
                        </button>
                      )}
                      <div className="mt-1 text-xs text-zinc-600">
                        {nameOf(a.created_by)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {tab === "requests" && (
            <div
              className="rounded-lg border border-zinc-800 bg-zinc-900"
              data-testid="customer-quote-requests"
            >
              <div className="flex flex-wrap items-center gap-3 border-b border-zinc-800 px-4 py-3">
                <span className="text-xs font-bold uppercase tracking-widest text-zinc-300">
                  Quote requests
                </span>
                <span className="font-mono-data text-[10px] text-zinc-600">
                  {requests.length} from this account
                </span>
              </div>
              {requests.length === 0 ? (
                <p className="px-4 py-6 text-sm text-zinc-500">
                  No quote requests from this account yet.
                </p>
              ) : (
                <>
                  <div className="hidden grid-cols-[1.2fr_1.4fr_1fr_0.9fr_0.7fr_0.9fr] gap-4 border-b border-zinc-800 bg-zinc-950 px-4 py-2.5 text-[10px] uppercase tracking-widest text-zinc-500 sm:grid">
                    <span>Request</span>
                    <span>Job address</span>
                    <span>Project</span>
                    <span>Came in</span>
                    <span>Files</span>
                    <span>Status</span>
                  </div>
                  {requests.map((r) => (
                    <Link
                      key={r.id}
                      to={`/leads/${r.id}`}
                      data-testid={`customer-request-row-${r.id}`}
                      className="grid gap-2 border-b border-zinc-800/70 px-4 py-3 transition-colors duration-150 last:border-0 hover:bg-zinc-800/40 sm:grid-cols-[1.2fr_1.4fr_1fr_0.9fr_0.7fr_0.9fr] sm:items-center sm:gap-4"
                    >
                      <span className="min-w-0">
                        <span className="font-mono-data block text-[11px] text-indigo-300">
                          {r.code}
                        </span>
                        <span className="block truncate text-xs text-zinc-500">
                          {r.source}
                        </span>
                      </span>
                      <span className="min-w-0 truncate text-sm text-zinc-200">
                        {r.address || "—"}
                      </span>
                      <span className="min-w-0 truncate text-xs text-zinc-400">
                        {r.project_type} · {r.timeline}
                      </span>
                      <span className="font-mono-data text-xs text-zinc-500">
                        {(r.created_at || "").slice(0, 10)}
                      </span>
                      <span className="text-xs text-zinc-400">
                        {(r.files || []).filter((f) => !f.is_deleted).length || "—"}
                      </span>
                      <span
                        className={`w-fit rounded-full border px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest ${
                          REQUEST_STYLE[r.status] || "border-zinc-700 text-zinc-300"
                        }`}
                      >
                        {r.status}
                      </span>
                    </Link>
                  ))}
                </>
              )}
            </div>
          )}
        </div>
      </main>

      <Dialog
        open={newContact !== null}
        onOpenChange={(o) => !o && setNewContact(null)}
      >
        <DialogContent
          data-testid="acct-new-contact-dialog"
          className="max-w-2xl border-zinc-800 bg-zinc-900"
        >
          <DialogHeader>
            <DialogTitle>
              {newContact?._idx != null ? "Edit contact" : "Add contact"}
            </DialogTitle>
          </DialogHeader>
          {newContact && (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  data-testid="new-contact-primary"
                  onClick={() =>
                    setNewContact({ ...newContact, primary: !newContact.primary })
                  }
                  className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest transition-colors duration-150 ${
                    newContact.primary
                      ? "border-amber-400/50 bg-amber-400/10 text-amber-200"
                      : "border-zinc-700 text-zinc-400 hover:border-zinc-500"
                  }`}
                >
                  <Star size={12} weight={newContact.primary ? "fill" : "bold"} />
                  {newContact.primary ? "Primary" : "Set primary"}
                </button>
                <button
                  type="button"
                  data-testid="new-contact-private"
                  onClick={() =>
                    setNewContact({ ...newContact, private: !newContact.private })
                  }
                  title="Hide this contact's quotes from other contacts on the portal"
                  className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest transition-colors duration-150 ${
                    newContact.private
                      ? "border-rose-400/50 bg-rose-400/10 text-rose-200"
                      : "border-zinc-700 text-zinc-400 hover:border-zinc-500"
                  }`}
                >
                  {newContact.private ? "Private" : "Make private"}
                </button>
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                {[
                  ["name", "Name", "Jane Smith", "text"],
                  ["role", "Role / title", "Project manager", "text"],
                  ["phone", "Phone", "(555) 123-4567", "tel"],
                  ["mobile", "Mobile", "(555) 987-6543", "tel"],
                  ["email", "Email", "jane@company.com", "email"],
                ].map(([k, label, ph, type]) => (
                  <label key={k} className="block">
                    <span className="mb-1 block text-[10px] font-bold uppercase tracking-widest text-zinc-500">
                      {label}
                    </span>
                    <Input
                      type={type}
                      data-testid={`new-contact-${k}`}
                      value={newContact[k] || ""}
                      placeholder={ph}
                      onChange={(e) =>
                        setNewContact({ ...newContact, [k]: e.target.value })
                      }
                      className="h-11 border-zinc-800 bg-zinc-950"
                    />
                  </label>
                ))}
              </div>
              <label className="block">
                <span className="mb-1 block text-[10px] font-bold uppercase tracking-widest text-zinc-500">
                  Notes
                </span>
                <Textarea
                  data-testid="new-contact-notes"
                  rows={2}
                  value={newContact.notes || ""}
                  placeholder="Best reached afternoons; handles approvals…"
                  onChange={(e) =>
                    setNewContact({ ...newContact, notes: e.target.value })
                  }
                  className="border-zinc-800 bg-zinc-950 text-sm"
                />
              </label>
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              data-testid="new-contact-cancel"
              onClick={() => setNewContact(null)}
            >
              Cancel
            </Button>
            <Button
              data-testid="new-contact-add"
              disabled={!newContact?.name?.trim()}
              onClick={() => {
                const { _idx, ...payload } = newContact;
                setDraft({
                  ...draft,
                  contacts:
                    _idx == null
                      ? [...draft.contacts, payload]
                      : draft.contacts.map((x, j) => (j === _idx ? payload : x)),
                });
                setNewContact(null);
              }}
            >
              {newContact?._idx != null ? "Save contact" : "Add contact"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={delOpen} onOpenChange={setDelOpen}>
        <AlertDialogContent className="border-zinc-800 bg-zinc-900">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {c.name}?</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              This permanently removes the account, its contacts and activity
              history. Jobs stay on the board. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              data-testid="cancel-customer-delete"
              className="border-zinc-700"
            >
              Keep account
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-customer-delete"
              onClick={remove}
              className="bg-red-600 text-white hover:bg-red-500"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      {c && (
        <MoveAccountDialog
          open={moveAllOpen}
          onClose={() => setMoveAllOpen(false)}
          onDone={() => window.location.reload()}
          endpoint={`/customers/${id}/move-all`}
          title="Move everything to another account"
          description="Moves all of this account's projects, jobs, quotes and invoices."
          confirmLabel="Move all"
          excludeId={id}
        />
      )}
    </div>
  );
}
